import React from 'react';
import { Clock } from 'lucide-react';
import { Service, AddOn } from '@/types';

interface ServicePickerProps {
  services: Service[];
  availableAddOns: AddOn[];
  selectedServiceIds: string[];
  selectedAddOnIds: string[];
  requiresAddOnSelection: boolean;
  isAddOnSelectionMissing: boolean;
  getPrice: (item: Service | AddOn) => number | string;
  onServiceToggle: (serviceId: string, checked: boolean) => void;
  onAddOnToggle: (addOnId: string, checked: boolean) => void;
}

export const ServicePicker: React.FC<ServicePickerProps> = ({
  services,
  availableAddOns,
  selectedServiceIds,
  selectedAddOnIds,
  requiresAddOnSelection,
  isAddOnSelectionMissing,
  getPrice,
  onServiceToggle,
  onAddOnToggle
}) => (
  <>
    <p className="text-xs text-slate-500 mb-3">
      Select one or more services — they will be combined into a single appointment.
    </p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {services.map((s) => {
        const price = getPrice(s);
        const isSelected = selectedServiceIds.includes(s.id);
        return (
          <div
            key={s.id}
            onClick={() => onServiceToggle(s.id, !isSelected)}
            className={`border p-5 rounded-xl cursor-pointer transition-all ${isSelected ? 'bg-teal-50 border-teal-500 ring-2 ring-teal-200' : 'bg-white border-slate-200 hover:border-slate-300'}`}
          >
            <div className="flex justify-between items-start">
              <h3 className="font-bold text-slate-900">{s.name}</h3>
              <span className="font-black text-teal-700">£{price} +VAT</span>
            </div>
            <p className="text-slate-500 text-xs mt-1 mb-4 h-8 line-clamp-2">{s.description}</p>
            <div className="flex items-center text-[10px] font-bold text-slate-400 uppercase gap-3">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> {s.durationMinutes} MINS
              </span>
              <span className="bg-slate-100 px-2 py-0.5 rounded-full">{s.category}</span>
              {isSelected && (
                <span className="bg-teal-600 text-white px-2 py-0.5 rounded-full">Selected</span>
              )}
            </div>
          </div>
        );
      })}
    </div>

    {selectedServiceIds.length > 0 && (
      <div className="mt-8 border-t pt-6">
        <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
          Enhance with Add-ons
          {requiresAddOnSelection && (
            <span className="text-[10px] font-bold text-amber-600 uppercase">Required</span>
          )}
        </h3>
        <div className="space-y-3">
          {availableAddOns.map((a) => (
            <label
              key={a.id}
              className={`flex items-center justify-between p-3 border rounded-xl cursor-pointer hover:bg-slate-50 transition-colors ${selectedAddOnIds.includes(a.id) ? 'bg-teal-50 border-teal-500' : 'border-slate-100'}`}
            >
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  className="w-5 h-5 text-teal-600 rounded"
                  checked={selectedAddOnIds.includes(a.id)}
                  onChange={(e) => onAddOnToggle(a.id, e.target.checked)}
                />
                <div>
                  <div className="font-bold text-slate-800 text-sm">
                    {a.name}{' '}
                    <span className="text-[10px] font-normal text-slate-400 ml-1">
                      +{a.durationMinutes}m
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-400">{a.description}</div>
                </div>
              </div>
              <span className="font-bold text-teal-600">+£{getPrice(a)} +VAT</span>
            </label>
          ))}
          {isAddOnSelectionMissing && (
            <p className="text-xs font-semibold text-amber-600">
              Select at least one add-on to continue with Health Surveillance.
            </p>
          )}
        </div>
      </div>
    )}
  </>
);
